import type { InviteCreated, InviteStatus, InviteView } from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент приглашений §104 п. 6 запросного канала §7.6 (stage9-signup-invites,
 * design.md Д6): список своих ссылок, выдача и отзыв. Всё под сессией и под
 * правом приглашать — `403` значит, что права нет или флаг `signup-invite`
 * выключен. Тот же стиль различения исходов статусом, что у `hidden-coins-api.ts`.
 */

const INVITES_BASE = "/api/invites";

const INVITE_STATUSES: ReadonlySet<string> = new Set<InviteStatus>([
  "pending",
  "used",
  "expired",
  "revoked",
]);

function isInviteView(value: unknown): value is InviteView {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.created_at_ms === "number" &&
    typeof value.expires_at_ms === "number" &&
    typeof value.status === "string" &&
    INVITE_STATUSES.has(value.status) &&
    (value.used_by_login === null || typeof value.used_by_login === "string")
  );
}

function isInviteCreated(value: unknown): value is InviteCreated {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.token === "string" &&
    typeof value.expires_at_ms === "number"
  );
}

export type InvitesListResult =
  | { status: "ok"; invites: InviteView[] }
  | { status: "unauthorized" }
  | { status: "forbidden" }
  | { status: "unavailable" };

/** Свои приглашения, новые сверху (порядок задаёт демон). */
export async function fetchInvites(): Promise<InvitesListResult> {
  let response: Response;
  try {
    response = await fetch(INVITES_BASE, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 403) {
    return { status: "forbidden" };
  }
  if (!response.ok) {
    console.error("список приглашений отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!Array.isArray(body) || !body.every(isInviteView)) {
      return { status: "unavailable" };
    }
    return { status: "ok", invites: body };
  } catch {
    return { status: "unavailable" };
  }
}

export type CreateInviteResult =
  | { status: "ok"; created: InviteCreated }
  | { status: "unauthorized" }
  | { status: "forbidden" }
  | { status: "unavailable" };

/** Выдача ссылки: токен виден только в этом ответе. */
export async function createInvite(): Promise<CreateInviteResult> {
  let response: Response;
  try {
    response = await fetch(INVITES_BASE, {
      method: "POST",
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 403) {
    return { status: "forbidden" };
  }
  if (!response.ok) {
    console.error("приглашение не выдано демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isInviteCreated(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", created: body };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * Исход отзыва. `404` — ссылка чужая или не существует (демон их не
 * различает), `409` — уже использована, истекла или отозвана.
 */
export type RevokeInviteResult =
  | { status: "ok" }
  | { status: "unauthorized" }
  | { status: "not_found" }
  | { status: "conflict" }
  | { status: "unavailable" };

export async function revokeInvite(id: string): Promise<RevokeInviteResult> {
  let response: Response;
  try {
    response = await fetch(`${INVITES_BASE}/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 204) {
    return { status: "ok" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 404) {
    return { status: "not_found" };
  }
  if (response.status === 409) {
    return { status: "conflict" };
  }
  console.error("приглашение не отозвано демоном", response.status);
  return { status: "unavailable" };
}
